import { Plus } from "lucide-react";
import React from "react";
import { isPracticeId } from "../../../../../onboarding/practice/practiceGuards";
import { scopedAnchor, usePracticeScope } from "../../../../../onboarding/practice/practiceScope";
import { useTranslation } from "react-i18next";
import {
    primaryText,
    secondaryText,
    greyTextColor,
    darkGreenTextColor,
    borderPrimary,
    placeholderPrimary,
    greenColorClass,
} from "../../../../../../styling/stylingHelper";
import { Subrecipe } from "../../../../../../services/subrecipes/subrecipesService";
import { eur } from "../../../../../orderhistory/utils";
import { formatSubrecipeUnitPrice } from "../../../../../../utils/ingredientGrouping";
import SourceRestaurantFilter from "./SourceRestaurantFilter";
import SourceRestaurantBadge from "../SourceRestaurantBadge";

interface SubrecipesTabProps {
    subrecipes: Subrecipe[];
    selectedSubrecipe: Subrecipe | null;
    onSelectSubrecipe: (subrecipe: Subrecipe) => void;
    quantityInputValue: string;
    onQuantityChange: (value: string, numericValue: number) => void;
    calculateEstimatedPrice: () => number | null;
    calculateSubrecipePrice: (subrecipe: Subrecipe, portionsNeeded?: number) => number | null;
    onCreateClick: () => void;
    searchQuery: string;
    currentRestaurantId: string;
    automationReview?: boolean;
    hideSourceFilter?: boolean;
    // Hide the "Create Subrecipe" shortcut (e.g. when the picker is already
    // nested inside a subrecipe editor). Defaults to shown.
    allowCreate?: boolean;
}

const SubrecipesTab: React.FC<SubrecipesTabProps> = ({
    subrecipes,
    selectedSubrecipe,
    onSelectSubrecipe,
    quantityInputValue,
    onQuantityChange,
    calculateEstimatedPrice,
    calculateSubrecipePrice,
    onCreateClick,
    searchQuery,
    currentRestaurantId,
    automationReview = false,
    hideSourceFilter = false,
    allowCreate = true,
}) => {
    const { t } = useTranslation();
    const practiceScope = usePracticeScope();
    if (automationReview) return <div className="min-h-0 overflow-y-auto">
        {!hideSourceFilter && <div className="p-3"><SourceRestaurantFilter currentRestaurantId={currentRestaurantId} /></div>}
        {allowCreate && <button type="button" onClick={onCreateClick} className={`flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-left text-xs hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark ${primaryText}`}>
            <Plus size={14} />{t("costPrice.automationRun.add.addSubrecipe")}
        </button>}
        {subrecipes.map(subrecipe => {
            const batchPrice = subrecipe.portions > 0 ? calculateSubrecipePrice(subrecipe) : null;
            const unitPrice = batchPrice !== null && Number.isFinite(batchPrice) && subrecipe.portions > 0
                ? formatSubrecipeUnitPrice(batchPrice / subrecipe.portions, subrecipe.portionsUnit, t("costPrice.automationRun.editor.portion")) : '—';
            return <button key={subrecipe.id} type="button" onClick={() => onSelectSubrecipe(subrecipe)}
            className={`flex min-h-[60px] w-full items-center justify-between gap-3 border-t px-4 py-2 text-left hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark ${borderPrimary}`}>
            <div className="min-w-0"><p className={`break-words text-sm ${primaryText}`}>{subrecipe.name}</p>
                <p className={`mt-0.5 text-xs ${greyTextColor}`}>{subrecipe.description}</p><SourceRestaurantBadge restaurantName={(subrecipe as any).sourceRestaurantName} />
            </div>
            <span className="shrink-0 text-right">
                <span className={`block text-sm font-medium ${primaryText}`} aria-label={t("costPrice.automationRun.add.unitPrice")}>{unitPrice}</span>
                <span className={`mt-1 block text-xs ${greyTextColor}`}>{t("costPrice.automationRun.add.perBatch", { amount: subrecipe.portions.toLocaleString(), unit: subrecipe.portionsUnit === "p" ? t("costPrice.addIngredient.piece") : subrecipe.portionsUnit })}</span>
            </span>
        </button>; })}
        {!subrecipes.length && <p className={`p-4 text-sm ${greyTextColor}`}>{t(searchQuery ? 'costPrice.addIngredient.noSubrecipesMatch' : 'costPrice.addIngredient.noSubrecipes')}</p>}
    </div>;
    return (
        <div className="flex flex-1 flex-col min-h-0 gap-3 overflow-y-auto">
            {!hideSourceFilter && <SourceRestaurantFilter currentRestaurantId={currentRestaurantId} />}
            {allowCreate && (
                <button
                    type="button"
                    data-onboarding={scopedAnchor(practiceScope, "cost-add-create-subrecipe")}
                    onClick={onCreateClick}
                    className={`w-full flex items-center justify-center gap-2 h-11 ${greenColorClass} rounded-lg text-[14px] font-semibold`}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 4.16663V15.8333" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.16699 10H15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t(automationReview ? 'costPrice.automationRun.add.addSubrecipe' : 'costPrice.addIngredient.createSubrecipe')}
                </button>
            )}
            {subrecipes.length === 0 ? (
                <div className={`p-4 text-center ${greyTextColor}`} data-onboarding={scopedAnchor(practiceScope, "cost-add-subrecipe-list")}>
                    {searchQuery ? t('costPrice.addIngredient.noSubrecipesMatch') : t('costPrice.addIngredient.noSubrecipes')}
                </div>
            ) : (
                subrecipes.map((subrecipe) => {
                    const isSelected = selectedSubrecipe?.id === subrecipe.id;
                    const totalPrice = calculateSubrecipePrice(subrecipe);

                    // Guided tour: the practice sauce is the only tagged row.
                    const onboardingIds = practiceScope ? [
                        'cost-add-subrecipe-list',
                        isPracticeId(subrecipe.id) ? 'cost-add-garlic-butter' : null,
                    ].filter(Boolean).join(' ') : undefined;

                    return (
                        <button
                            key={subrecipe.id}
                            type="button"
                            data-onboarding={onboardingIds || undefined}
                            onClick={() => onSelectSubrecipe(subrecipe)}
                            className={`w-full text-left rounded-lg border py-[11px] pl-3 pr-4 bg-background dark:bg-background-dark flex flex-col transition-colors ${
                                isSelected
                                    ? "border-darkGreenColor dark:border-darkGreenColor-dark"
                                    : borderPrimary
                            }`}
                        >
                            <div className="mb-3 flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                    <p className={`text-[16px] leading-6 font-semibold tracking-[-0.16px] ${primaryText}`}>
                                        {subrecipe.name}
                                    </p>
                                    <SourceRestaurantBadge restaurantName={(subrecipe as any).sourceRestaurantName} />
                                </div>
                                {isSelected && (() => {
                                    const estimatedPrice = calculateEstimatedPrice();
                                    return estimatedPrice !== null ? (
                                        <p className={`text-[16px] leading-6 font-semibold ${darkGreenTextColor}`}>
                                            {eur(estimatedPrice)}
                                        </p>
                                    ) : null;
                                })()}
                            </div>

                            {subrecipe.description && (
                                <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal mb-2`}>
                                    {subrecipe.description}
                                </p>
                            )}

                            {totalPrice !== null && (
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                        {t("costPrice.automationRun.add.unitPrice")}
                                    </p>
                                    <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                        {formatSubrecipeUnitPrice(totalPrice / subrecipe.portions, subrecipe.portionsUnit)}
                                    </p>
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-3">
                                <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                    {t("costPrice.automationRun.add.ingredients")}
                                </p>
                                <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                    {subrecipe.ingredients.length}
                                </p>
                            </div>

                            <div className="flex items-center justify-between gap-3 my-2">
                                <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                    {t("costPrice.automationRun.add.makes")}
                                </p>
                                <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                    {automationReview
                                        ? t("costPrice.automationRun.add.perBatch", { amount: subrecipe.portions.toLocaleString(), unit: subrecipe.portionsUnit === "p" ? t("costPrice.addIngredient.piece") : subrecipe.portionsUnit })
                                        : <>{subrecipe.portions}{subrecipe.portionsUnit === 'g' ? 'g' : subrecipe.portionsUnit === 'ml' ? 'ml' : ''}</>}
                                </p>
                            </div>

                            {isSelected && !automationReview && (
                                <div className="mt-3">
                                    <p className={`${secondaryText} mb-3 text-[16px] leading-5 font-medium`}>
                                        {t("costPrice.automationRun.add.amount")}
                                    </p>
                                    <div
                                        className="flex items-center justify-between gap-2 rounded-lg border border-borderGrey dark:border-borderGrey-dark bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 h-11"
                                        data-onboarding={practiceScope && isPracticeId(subrecipe.id) ? 'cost-add-subrecipe-qty' : undefined}
                                    >
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min={0}
                                            step="any"
                                            placeholder={`0.0 ${subrecipe.portionsUnit === 'g' ? 'g' : subrecipe.portionsUnit === 'ml' ? 'ml' : 'portions'}`}
                                            value={quantityInputValue}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                                    onQuantityChange(value, value === '' ? 0 : parseFloat(value) || 0);
                                                }
                                            }}
                                            className={`flex-1 bg-transparent dark:bg-transparent text-[14px] leading-5 font-medium ${secondaryText} ${placeholderPrimary} outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-moz-appearance]:textfield`}
                                        />
                                        <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                            {subrecipe.portionsUnit === 'g' ? 'g' : subrecipe.portionsUnit === 'ml' ? 'ml' : 'portions'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </button>
                    );
                })
            )}
        </div>
    );
};

export default SubrecipesTab;

